import React, { useEffect, useState, useRef } from 'react';
import { api } from '../api/client';
import {
  PageHeader, Button, Card, Badge, Modal, Input, Textarea, EmptyState, formatDate,
} from '../components/UI';

export default function Classes() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);

  useEffect(() => { refresh(); }, []);

  async function refresh() {
    setLoading(true);
    try { setList(await api.classes.list()); }
    catch (e) { alert(e.message); }
    finally { setLoading(false); }
  }

  return (
    <>
      <PageHeader
        eyebrow="Catálogo"
        title="Clases"
        subtitle={`${list.length} clases · ${list.filter(c => c.is_published).length} publicadas`}
        actions={<Button onClick={() => setCreating(true)}>+ Nueva clase</Button>}
      />

      {loading ? (
        <EmptyState>Cargando…</EmptyState>
      ) : list.length === 0 ? (
        <EmptyState>Todavía no hay clases. Creá la primera con el botón de arriba.</EmptyState>
      ) : (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
          gap: 16,
        }}>
          {list.map(c => (
            <ClassTile key={c.id} cls={c} onClick={() => setEditing(c.id)} />
          ))}
        </div>
      )}

      {creating && (
        <ClassForm
          onClose={() => setCreating(false)}
          onSaved={(id) => { setCreating(false); refresh(); setEditing(id); }}
        />
      )}

      {editing && (
        <ClassDetail
          classId={editing}
          onClose={() => setEditing(null)}
          onChanged={refresh}
        />
      )}
    </>
  );
}

// ──────────────────────────────────────────────────────────
function ClassTile({ cls, onClick }) {
  const apiBase = process.env.REACT_APP_API_URL || '/api';
  const imgUrl = cls.image_filename ? `${apiBase}/images/${cls.image_filename}` : null;

  return (
    <div onClick={onClick} style={{
      border: '1px solid var(--border)',
      borderRadius: 6,
      overflow: 'hidden',
      cursor: 'pointer',
      background: '#fff',
      transition: 'border-color 0.15s',
    }}>
      <div style={{
        height: 140,
        background: imgUrl ? `url(${imgUrl}) center/cover` : 'var(--sand-2)',
        display: 'flex', alignItems: 'flex-end', padding: 10,
        position: 'relative',
      }}>
        {!cls.is_published && (
          <div style={{
            position: 'absolute', top: 10, right: 10,
            padding: '3px 8px',
            background: 'rgba(44,42,38,0.75)',
            color: '#F5F0E8',
            fontSize: 9, letterSpacing: '0.15em',
            textTransform: 'uppercase',
            borderRadius: 12,
          }}>Borrador</div>
        )}
      </div>
      <div style={{ padding: 14 }}>
        <div style={{ fontSize: 9, letterSpacing: '0.22em', textTransform: 'uppercase', color: 'var(--earth)', marginBottom: 4 }}>
          {cls.code}
        </div>
        <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 18, marginBottom: 8 }}>
          {cls.title}
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 11, color: 'var(--ink-soft)' }}>
          <span>{formatDate(cls.class_date)}</span>
          <span style={dotStyle} />
          <span>{cls.duration_minutes} min</span>
          <span style={dotStyle} />
          <span>{cls.audio_count} audio{cls.audio_count !== 1 ? 's' : ''}</span>
        </div>
        {cls.play_count > 0 && (
          <div style={{ marginTop: 8, fontSize: 11, color: 'var(--moss)' }}>
            ♪ {cls.play_count} audiciones · {cls.unique_listeners} oyentes
          </div>
        )}
      </div>
    </div>
  );
}

const dotStyle = {
  width: 2, height: 2, borderRadius: '50%', background: 'var(--stone)',
};

// ──────────────────────────────────────────────────────────
function ClassForm({ initial, onClose, onSaved }) {
  const isEdit = !!initial;
  const today = new Date().toISOString().slice(0, 10);
  const [code, setCode] = useState(initial?.code || '');
  const [title, setTitle] = useState(initial?.title || '');
  const [description, setDescription] = useState(initial?.description || '');
  const [classDate, setClassDate] = useState(initial?.class_date?.slice(0,10) || today);
  const [isPublished, setIsPublished] = useState(initial?.is_published ?? 0);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  async function submit(e) {
    e.preventDefault();
    setErr(''); setBusy(true);
    try {
      const data = { code, title, description, class_date: classDate, is_published: isPublished };
      if (isEdit) {
        await api.classes.update(initial.id, data);
        onSaved(initial.id);
      } else {
        const r = await api.classes.create(data);
        onSaved(r.id);
      }
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  }

  return (
    <Modal open onClose={onClose} title={isEdit ? 'Editar clase' : 'Nueva clase'}>
      <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 16 }}>
          <Input label="Código" value={code} onChange={e => setCode(e.target.value)} required placeholder="GC-007" />
          <Input label="Fecha" type="date" value={classDate} onChange={e => setClassDate(e.target.value)} required />
        </div>
        <Input label="Título" value={title} onChange={e => setTitle(e.target.value)} required />
        <Textarea label="Descripción" value={description} onChange={e => setDescription(e.target.value)} rows={3} />
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }}>
          <input type="checkbox" checked={!!isPublished} onChange={e => setIsPublished(e.target.checked ? 1 : 0)} />
          Publicada (visible para los pacientes)
        </label>
        {err && <div style={{ fontSize: 12, color: 'var(--danger)' }}>{err}</div>}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Button type="button" variant="ghost" onClick={onClose}>Cancelar</Button>
          <Button type="submit" disabled={busy}>{busy ? 'Guardando…' : 'Guardar'}</Button>
        </div>
      </form>
    </Modal>
  );
}

// ──────────────────────────────────────────────────────────
function ClassDetail({ classId, onClose, onChanged }) {
  const [data, setData] = useState(null);
  const [showEdit, setShowEdit] = useState(false);
  const audioInputRef = useRef(null);
  const imageInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => { load(); }, [classId]);

  async function load() {
    try { setData(await api.classes.get(classId)); }
    catch (e) { alert(e.message); }
  }

  async function uploadAudio(files) {
    if (!files?.length) return;
    setUploading(true);
    try {
      await api.audios.upload(classId, files);
      await load();
      onChanged();
    } catch (e) { alert(e.message); }
    finally { setUploading(false); }
  }

  async function uploadImage(file) {
    if (!file) return;
    setUploading(true);
    try {
      await api.classes.uploadImage(classId, file);
      await load();
      onChanged();
    } catch (e) { alert(e.message); }
    finally { setUploading(false); }
  }

  async function removeAudio(id) {
    if (!window.confirm('¿Eliminar este audio?')) return;
    try {
      await api.audios.remove(id);
      await load();
      onChanged();
    } catch (e) { alert(e.message); }
  }

  async function moveAudio(audio, dir) {
    const newOrder = audio.order_index + (dir === 'up' ? -1 : 1);
    try {
      await api.audios.update(audio.id, { order_index: newOrder });
      await load();
    } catch (e) { alert(e.message); }
  }

  async function deleteClass() {
    if (!data) return;
    if (!window.confirm(`¿Eliminar la clase "${data.class.title}"?\nSe borrarán todos los archivos asociados. Esta acción no se puede deshacer.`)) return;
    try {
      await api.classes.remove(classId);
      onClose();
      onChanged();
    } catch (e) { alert(e.message); }
  }

  if (!data) return <Modal open onClose={onClose}><EmptyState>Cargando…</EmptyState></Modal>;

  const cls = data.class;
  const apiBase = process.env.REACT_APP_API_URL || '/api';
  const imgUrl = cls.image_filename ? `${apiBase}/images/${cls.image_filename}` : null;

  return (
    <Modal open onClose={onClose} title={cls.title} maxWidth={780}>
      <div style={{ marginBottom: 6 }}>
        <Badge tone={cls.is_published ? 'success' : 'neutral'}>
          {cls.is_published ? 'Publicada' : 'Borrador'}
        </Badge>
        <span style={{ marginLeft: 10, fontSize: 9, letterSpacing: '0.22em', textTransform: 'uppercase', color: 'var(--earth)' }}>
          {cls.code}
        </span>
      </div>
      <div style={{ fontSize: 13, color: 'var(--ink-soft)', marginBottom: 24 }}>
        {formatDate(cls.class_date)} · {cls.duration_minutes} minutos
      </div>

      {cls.description && (
        <div style={{ marginBottom: 24, fontSize: 14, color: 'var(--ink-soft)', lineHeight: 1.6 }}>
          {cls.description}
        </div>
      )}

      {/* Imagen */}
      <div style={{ marginBottom: 28 }}>
        <SectionHeader>Imagen de portada</SectionHeader>
        <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
          <div style={{
            width: 160, height: 110,
            background: imgUrl ? `url(${imgUrl}) center/cover` : 'var(--sand-2)',
            border: '1px solid var(--border)',
            borderRadius: 6,
            display: 'grid', placeItems: 'center',
            color: 'var(--stone)', fontSize: 11,
          }}>
            {!imgUrl && 'Sin imagen'}
          </div>
          <div style={{ flex: 1 }}>
            <input
              type="file"
              accept="image/*"
              ref={imageInputRef}
              onChange={e => uploadImage(e.target.files[0])}
              style={{ display: 'none' }}
            />
            <Button size="sm" variant="secondary" onClick={() => imageInputRef.current?.click()}>
              {imgUrl ? 'Cambiar imagen' : 'Subir imagen'}
            </Button>
            <div style={{ fontSize: 11, color: 'var(--stone)', marginTop: 6 }}>
              JPG, PNG o WebP · máx 5 MB
            </div>
          </div>
        </div>
      </div>

      {/* Audios */}
      <div style={{ marginBottom: 28 }}>
        <SectionHeader>Audios ({data.audios.length})</SectionHeader>
        {data.audios.length === 0 ? (
          <EmptyState>No hay audios todavía. Subí el primero con el botón de abajo.</EmptyState>
        ) : (
          <div style={{ border: '1px solid var(--border)', borderRadius: 6 }}>
            {data.audios.map((a, i) => (
              <div key={a.id} style={{
                display: 'flex', alignItems: 'center', gap: 12,
                padding: '10px 14px',
                borderBottom: i < data.audios.length - 1 ? '1px solid var(--border)' : 'none',
              }}>
                <div style={{
                  width: 22, height: 22, borderRadius: '50%',
                  background: 'var(--sand-2)',
                  display: 'grid', placeItems: 'center',
                  fontSize: 11, fontWeight: 400,
                }}>
                  {a.order_index}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontFamily: "'JetBrains Mono', monospace", overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {a.filename}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--ink-soft)' }}>
                    {Math.floor(a.duration_seconds / 60)}:{String(a.duration_seconds % 60).padStart(2,'0')}
                    {' · '}
                    {(a.file_size_bytes / 1024 / 1024).toFixed(1)} MB
                  </div>
                </div>
                <Button size="sm" variant="ghost"
                        disabled={i === 0}
                        onClick={() => moveAudio(a, 'up')}>↑</Button>
                <Button size="sm" variant="ghost"
                        disabled={i === data.audios.length - 1}
                        onClick={() => moveAudio(a, 'down')}>↓</Button>
                <Button size="sm" variant="danger" onClick={() => removeAudio(a.id)}>Eliminar</Button>
              </div>
            ))}
          </div>
        )}
        <input
          type="file"
          accept=".mp3,.m4a,audio/*"
          multiple
          ref={audioInputRef}
          onChange={e => uploadAudio(e.target.files)}
          style={{ display: 'none' }}
        />
        <div style={{ marginTop: 12 }}>
          <Button onClick={() => audioInputRef.current?.click()} disabled={uploading}>
            {uploading ? 'Subiendo…' : '+ Subir audio(s)'}
          </Button>
          <span style={{ fontSize: 11, color: 'var(--stone)', marginLeft: 12 }}>
            MP3 o M4A · máx 100 MB cada uno
          </span>
        </div>
      </div>

      {/* Acciones */}
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        paddingTop: 20, borderTop: '1px solid var(--border)',
      }}>
        <Button variant="danger" size="sm" onClick={deleteClass}>Eliminar clase</Button>
        <div style={{ display: 'flex', gap: 8 }}>
          <Button variant="secondary" onClick={() => setShowEdit(true)}>Editar datos</Button>
          <Button onClick={onClose}>Cerrar</Button>
        </div>
      </div>

      {showEdit && (
        <ClassForm
          initial={cls}
          onClose={() => setShowEdit(false)}
          onSaved={() => { setShowEdit(false); load(); onChanged(); }}
        />
      )}
    </Modal>
  );
}

function SectionHeader({ children }) {
  return (
    <div style={{
      fontSize: 11, fontWeight: 300, letterSpacing: '0.18em',
      textTransform: 'uppercase', color: 'var(--earth)',
      marginBottom: 12,
    }}>{children}</div>
  );
}
